import {IconButton, ListItemSecondaryAction, Tooltip} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import React from "react";
import {useHistory} from 'react-router-dom';
import {generalStore} from "../../../index";
import {useStores} from "../../../Hooks";
import ConditionalComponent from "../ConditionalComponent";

const Actions = ({result, handleClose, editPromissions, fullpath, isHoveredSearch, setIsHoveredSearch}) => {
    const {workspace} = generalStore;
    const {modalStore, projectStore} = useStores();
    const history = useHistory();

    const getFolderPath = (result) => (result.NestedFolderRoute || '').split('/').slice(1).join('/');

    const buildItemRoute = (result, itemId) => {
        const folderPath = getFolderPath(result);
        const base = `/project/${result.ProjectRef}/procedures`;
        return folderPath ? `${base}/${folderPath}/${itemId}` : `${base}/${itemId}`;
    };

    const buildParentRoute = (result) => {
        const folderPath = getFolderPath(result);
        const base = `/project/${result.ProjectRef}/procedures`;
        return folderPath ? `${base}/${folderPath}` : base;
    };

    const openSharing = () => {
        modalStore.isShareProcedureModalOpen = true;
        modalStore.modalData = {
            sharingId: result.ProcedureIdentifier,
            procedureName: result.ProcedureName,
            procedureType: result.ProcedureType,
        };
    };

    const handleDashboardClick = () => {
        history.push(`/project/${result.ProjectIdentifier}/dashboard`);
        handleClose()
    };

    const handleSettingsClick = () => {
        history.push(`/project/${result.ProjectIdentifier}/settings`);
        handleClose()
    };

    const editProcedures = () => {
        if (!(result?.ProcedureType === 0 || result.ProcedureType === 3 || result.ProcedureType === 1)) return;
        history.push(buildItemRoute(result, result.ProcedureIdentifier));
        handleClose()
    };

    const runProcedures = () => {
        if (!(result?.ProcedureType === 0 || result.ProcedureType === 3 || result.ProcedureType === 1) || !result?.IsPublished) return;
        let url = `/view/${workspace}/${result.ProcedureIdentifier}`
        if (result.mirrored) {
            url = `/project/${result.ProjectRef}/procedures/${result.ProcedureIdentifier}`
        }
        window.open(url, '_blank');
    };

    const showProcedures = () => {
        history.push(buildParentRoute(result));
        handleClose()
    };

    const shareProcedures = () => {
        if (!result?.IsPublished) return;
        openSharing();
    }

    const openFolder = async () => {
        if (result.type === 'folders') {
            history.push(buildItemRoute(result, result.FolderIdentifier));
            handleClose()
            return;
        }

        await history.push(`/project/${result.ProjectRef}/dashboard`);
        await new Promise(resolve => setTimeout(resolve, 1000))
        await getPathToItem(result._id, result.ProjectRef);
        handleClose()
        modalStore.isMediaLibraryModalOpen = true;
    }

    const showFolder = async () => {
        if (result.type === 'folders') {
            history.push(buildParentRoute(result));
            handleClose()
            return;
        }

        await history.push(`/project/${result.ProjectRef}/dashboard`);
        await new Promise(resolve => setTimeout(resolve, 1000))
        if (!result?.ParentFolder) {
            projectStore.mediaLibraryPathArray = [projectStore.mediaLibraryPathArray[0]]
            modalStore.isMediaLibraryModalOpen = true;
            return;
        }
        await getPathToItem(result.ParentFolder, result.ProjectRef);
        handleClose()
        modalStore.isMediaLibraryModalOpen = true;
    }

    const showMediaLibrary = async () => {
        await history.push(`/project/${result.ProjectRef}/dashboard`);
        await new Promise(resolve => setTimeout(resolve, 1000))
        if (!result?.ParentFolder) {
            projectStore.mediaLibraryPathArray = [projectStore.mediaLibraryPathArray[0]]
            modalStore.isMediaLibraryModalOpen = true;
            return;
        }
        await getPathToItem(result.ParentFolder, result.ProjectRef);
        handleClose()
        modalStore.isMediaLibraryModalOpen = true;
    }

    async function getPathToItem(folderId, projectId) {
        const {mediaFolders} = await projectStore.getAllProjectFilesAndFolders(projectId);
        let currentFolderId = folderId;
        let foldersPathArray = [];

        function findFolderById(folderId) {
            const foundFolder = mediaFolders.find(folder => folder._id === folderId);
            if (foundFolder) return foundFolder;
            return null;
        }

        while (currentFolderId && currentFolderId !== 'root') {
            const currentFolder = findFolderById(currentFolderId);
            if (!currentFolder) break;

            foldersPathArray.push({
                folderId: currentFolder._id,
                folderName: currentFolder.FolderName,
            });

            currentFolderId = currentFolder.ParentFolder;
        }

        const lib = [projectStore.mediaLibraryPathArray[0]]
        for (let i = foldersPathArray.length - 1; i >= 0; i--) {
            lib.push(foldersPathArray[i]);
        }
        projectStore.mediaLibraryPathArray = lib
    }

    return (
        <ListItemSecondaryAction className="search-global-actions">
            <ConditionalComponent if={result.type === 'procedures'}>
                <Tooltip
                    arrow
                    title={result?.IsPublished ? "Run/Open item" : "Unpublished"}
                    classes="search-global-tooltip"
                    disableInteractive
                >
                    <IconButton
                        edge="end"
                        aria-label="play"
                        className={result?.IsPublished ? "search-global-runButton" : "search-global-runButtonDisables"}
                        onClick={(e) => {
                            e.stopPropagation();
                            runProcedures();
                        }}
                    >
                        <PlayArrowIcon className="search-global-largeIcon"/>
                    </IconButton>
                </Tooltip>
                <Tooltip
                    onMouseEnter={() => setIsHoveredSearch(true)}
                    onMouseLeave={() => setIsHoveredSearch(false)}
                    title={
                        <React.Fragment>
                            {isHoveredSearch && fullpath.length > 0
                                ? <span>{fullpath}</span>
                                : <span>{"Loading..."}</span>}
                        </React.Fragment>
                    }
                    disableInteractive
                    classes="search-global-tooltip"
                    arrow
                >
                    <IconButton
                        edge="end"
                        aria-label="search"
                        className={"search-global-actionButton"}
                        onClick={(e) => {
                            e.stopPropagation();
                            showProcedures();
                        }}
                    >
                        <SearchOutlinedIcon/>
                    </IconButton>
                </Tooltip>
                <Tooltip
                    title={result?.IsPublished ? "Share" : "Unpublished"}
                    classes="search-global-tooltip"
                    arrow
                    disableInteractive
                >
                    <IconButton
                        edge="end"
                        aria-label="share"
                        className={result?.IsPublished ? "search-global-actionButton" : "search-global-actionButtonDisabled"}
                        onClick={(e) => {
                            e.stopPropagation();
                            shareProcedures();
                        }}
                    >
                        <ShareOutlinedIcon/>
                    </IconButton>
                </Tooltip>
                <ConditionalComponent if={editPromissions && !result?.mirrored}>
                    <Tooltip title="Edit" classes="search-global-tooltip" arrow disableInteractive>
                        <IconButton
                            edge="end"
                            aria-label="edit"
                            className={"search-global-actionButton"}
                            onClick={(e) => {
                                e.stopPropagation();
                                editProcedures();
                            }}
                        >
                            <EditOutlinedIcon/>
                        </IconButton>
                    </Tooltip>
                </ConditionalComponent>
            </ConditionalComponent>

            <ConditionalComponent if={result.type === 'folders' || result.type === 'mediafolders'} >
                <Tooltip
                    onMouseEnter={() => setIsHoveredSearch(true)}
                    onMouseLeave={() => setIsHoveredSearch(false)}
                    title={
                        <React.Fragment>
                            {isHoveredSearch && fullpath.length > 0
                                ? <span>{fullpath}</span>
                                : <span>{"Loading..."}</span>}
                        </React.Fragment>
                    }
                    disableInteractive
                    classes="search-global-tooltip"
                    arrow
                >
                    <IconButton
                        edge="end"
                        aria-label="search"
                        className={"search-global-actionButton"}
                        onClick={(e) => {
                            e.stopPropagation();
                            showFolder();
                        }}
                    >
                        <SearchOutlinedIcon/>
                    </IconButton>
                </Tooltip>
                <Tooltip
                    title="Open"
                    classes="search-global-tooltip"
                    arrow
                    disableInteractive
                >
                    <IconButton
                        edge="end"
                        aria-label="folder"
                        className={"search-global-actionButton"}
                        onClick={(e) => {
                            e.stopPropagation();
                            openFolder();
                        }}
                    >
                        <FolderCopyOutlinedIcon/>
                    </IconButton>
                </Tooltip>
            </ConditionalComponent>

            <ConditionalComponent if={result.type === 'fileschemas'}>
                <Tooltip
                    title="Open"
                    classes="search-global-tooltip"
                    arrow
                    disableInteractive
                >
                    <IconButton edge="end" aria-label="folder" className={"search-global-actionButton"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showMediaLibrary();
                                }}>
                        <FolderCopyOutlinedIcon/>
                    </IconButton>
                </Tooltip>
            </ConditionalComponent>

            <ConditionalComponent if={result.type === 'projects'}>
                <Tooltip title="Open" classes="search-global-tooltip" arrow disableInteractive>
                    <IconButton edge="end" aria-label="folder" className={"search-global-actionButton"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDashboardClick();
                                }}>
                        <FolderCopyOutlinedIcon/>
                    </IconButton>
                </Tooltip>
                <ConditionalComponent if={editPromissions}>
                    <Tooltip
                        title={"Edit"}
                        classes="search-global-tooltip"
                        arrow
                        disableInteractive
                    >
                        <IconButton
                            edge="end"
                            aria-label="edit"
                            className={"search-global-actionButton"}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSettingsClick();
                            }}
                        >
                            <EditOutlinedIcon/>
                        </IconButton>
                    </Tooltip>
                </ConditionalComponent>
            </ConditionalComponent>
        </ListItemSecondaryAction>
    );
}
export default Actions;
